import { Logger } from '@/util/logger';
import {
  EasingType,
  Easing
}                 from './easing';

/**
 *
 * @class EasingLookupTable
 *
 * This is a helper for working with easing curves.  It pre-calculates a lookup table of
 * possible positions throughout the range you are interpolating, and lets you work with
 * positions as if they were just linear interpolated values. But...LINEAR isn't the
 * only mode :)  You can use any of the available easing curves.
 *
 * The start and end values are the brackets for the range you are interpolating on.
 *
 * The steps value is just how many increments you want there to be in the range you
 * are interpolating on.
 *
 * The kind parameter is used to select the easing curve to use.  By default, it's LINEAR.
 *
 * We also add lookup table support, so we can to "reverse lookup" and pre-calculate
 * the values for speed.
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      June 2023
 *
 */

/** lookup table for pre-calculated easing */
export class EasingLookupTable {

  /** the kind of easing curve to use */
  public kind: EasingType      = EasingType.LINEAR;

  /** how many increments do we have? */
  public steps: number         = 100;

  /** where does the easing begin? */
  public start: number         = 0;

  /** where does the easing end? */
  public end: number           = 1.0;

  /** the look-up table for the pre-calculated values */
  private lut: {progress: number, tween: number, index: number}[]        = [];

  /** the step iterator position */
  private iterator: number     = 0;

  /** standard constructor */
  constructor(init?: Partial<EasingLookupTable>) {

    let elt: EasingLookupTable = this;

    /* basic initialization */

    Object.assign(elt, init);

    /* generate the lookup table ... */

    let range: number = Math.abs(elt.end - elt.start);
    let step: number  = range / elt.steps;

    /* pre-calculate for each step */

    for (let index: number = 0; index <= elt.steps; index++) {

      let progress: number = (index * step) / range;

      if (progress > 1.0) {

        break;
      }

      /* find the easing value for this increment */

      let tween: number = Easing.ease(elt.kind, progress);

      /* build out the lookup table */

      elt.lut.push( { progress, tween, index } );

    }

  }

  /** force the kind of easing curve */
  setKind(kind: EasingType): EasingLookupTable {

    this.kind = kind;

    return this;
  }

  /** fetch the kind of easing curve */
  getKind(): EasingType {

    return this.kind;
  }

  /** force the number of steps or increments to be in the table */
  setSteps(steps: number): EasingLookupTable {

    this.steps = steps;

    return this;
  }

  /** fetch the number of steps in the table */
  getSteps(): number {

    return this.steps;
  }

  /** force the start of the range we are interpolating */
  setStart(start: number): EasingLookupTable {

    this.start = start;

    return this;
  }

  /** fetch the start of the range we are interpolating */
  getStart(): number {

    return this.start;
  }

  /** force the end of the range we are interpolating */
  setEnd(end: number): EasingLookupTable {

    this.end = end;

    return this;
  }

  /** fetch the end of the range we are interpolating */
  getEnd(): number {

    return this.end;
  }

  /** build an easing look up table (LUT) */
  public build(): EasingLookupTable {

    return new EasingLookupTable({
      kind:  this.kind,
      steps: this.steps,
      start: this.start,
      end:   this.end
    });
  }

  /**
   * for a value between 0.0 and 1.0, find the corresponding tweened value
   * in our range from getStart() to getEnd().
   *
   * Note there is no pre-calculation involved. This value is calculated
   * direct from the easing curve every time you call it.
   *
   */
  tween(progress: number): number {

    let elt: EasingLookupTable = this;

    /* sanitize ... */

    if (progress < 0) {

      progress = 0;
    }

    if (progress > 1.0) {

      progress = 1.0;
    }

    /* tween it! */

    return Easing.ease(elt.kind, progress) * Math.abs(elt.end - elt.start);
  }

  /**
   * find the look-up table pre-calculated position which is closest to the
   * given progress.
   *
   * @param progress the normalized value to match on
   *
   * @param matchOnTween match on the tween value (reverse lookup)
   *
   * @private
   */
  private indexOf(progress: number, matchOnTween: boolean = false): number {

    let elt: EasingLookupTable = this;

    /*
     * the lookup table is sorted, because we generated it in increasing
     * order.
     *
     * https://stackoverflow.com/questions/8584902/get-the-closest-number-out-of-an-array
     *
     */

    if (!elt.lut || elt.lut.length == 0) {

      /* nothing to find yet */

      return 0;
    }

    if (progress == 0) {

      return 0;
    }

    if (progress >= 1.0) {

      return elt.lut.length - 1;
    }

    let from: number = 0, until: number = elt.lut.length - 1;

    while (true) {

      const cursor: number = Math.floor((from + until) / 2);

      if (cursor === from) {

        const diff1: number = progress - (matchOnTween ? elt.lut[cursor].tween : elt.lut[from].progress);
        const diff2: number = (matchOnTween ? elt.lut[cursor].tween : elt.lut[until].progress) - progress;

        return diff1 <= diff2 ? from : until;
      }

      const found: number = (matchOnTween ? elt.lut[cursor].tween : elt.lut[cursor].progress);

      if (found === progress) {

        return cursor;
      }

      if (found > progress) {

        until = cursor;

      } else if (found < progress) {

        from = cursor;
      }

    }
  }

  /**
   *
   * return a normalized (0..1) value given a value that is between
   * getStart and getEnd().
   *
   */
  public normalize(value: number): number {

    let elt: EasingLookupTable = this;

    let s: number = elt.getStart();
    let e: number = elt.getEnd();

    if (s > e) {

      let tmp: number = s;
      s = e;
      e = tmp;
    }

    if (value < s) {

      value = s;
    }

    if (value > e) {

      value = e;
    }

    return value / elt.range();
  }

  /** fetch the range we are interpolating over */
  public range(): number {

    let elt: EasingLookupTable = this;

    return Math.abs(elt.end - elt.start);
  }

  /**
   * for a value between getStart() and getEnd(), find the corresponding tweened value
   * in our range from getStart() to getEnd().
   *
   * Note that we will use the closed pre-calculated value.
   *
   * @param progress a value between getStart() and getEnd()
   */
  lookUp(progress: number): number {

    let elt: EasingLookupTable = this;

    /* normalize */

    progress = elt.normalize(progress);

    /* now give back the (closest) tweened value, but scaled up to our range */

    return Easing.ease(
      elt.kind,
      elt.lut[elt.indexOf(progress)].progress * elt.range()
    );
  }

  /**
   * for a value between getStart() and getEnd(), find the input to lookUp()
   * that generates this value.
   *
   * @param value a value generated by lookUp()
   *
   * @return the parameter given to lookUp() to generate this value.
   */
  reverseLookUp(value: number): number {

    let elt: EasingLookupTable = this;

    let tween: number = elt.normalize(value);

    return elt.lut[elt.indexOf(tween, true)].progress * elt.range();
  }

  /**
   * set iteration to start at this position (a value between getStart() and getEnd()
   *
   * @param value a value generated by lookUp()
   */
  reset(value: number = 0): number {

    let elt: EasingLookupTable = this;

    if (!value) {

      value = 0;
    }

    if (value === 0) {

      /* just start at beginning */

      elt.iterator = 0;

      return elt.value();
    }

    /* start at the closest position */

    elt.iterator = elt.indexOf(elt.normalize(value));

    return elt.value();
  }

  /** fetch the current value of the range iterator */
  public value(): number {

    let elt: EasingLookupTable = this;

    return elt.lut[elt.iterator].progress * elt.range();
  }

  /** fetch the raw tween value for the current iterator position */
  public getTween(): number {

    let elt: EasingLookupTable = this;

    return elt.lut[elt.iterator].tween;
  }

  /** fetch the raw progrss value for the current iterator position */
  public getProgress(): number {

    let elt: EasingLookupTable = this;

    return elt.lut[elt.iterator].progress;
  }

  /**
   * iterate through the easing, if you start at the number of steps, there will be getSteps() number of steps
   * you can make (backwards). (set when the table is built).
   *
   * @param decrement turn back the iterator this much before returning the next step. If you
   * provide 0, then we start and at the end of the table and use decrement of 1.
   *
   */
  previous(decrement: number = 1): number {

    let elt: EasingLookupTable = this;

    /* sanitize */

    if (decrement === 0) {

      elt.iterator = elt.lut.length - 1;
      decrement    = 1;
    }

    /* decrement */

    elt.iterator -= decrement;

    if (elt.iterator < 0) {

      elt.iterator = 0;
    }

    /* fetch the step value */

    return elt.value();
  }

  /**
   * iterate through the easing, if you start at 0, there will be getSteps() number of steps
   * you can make. (set when the table is built).
   *
   * @param increment advance the iterator this much before returning the next step. If you
   * provide 0, then we start and at the beginning of the table and use increment of 1.
   *
   */
  next(increment: number = 1): number {

    let elt: EasingLookupTable = this;

    /* sanitize */

    if (increment === 0) {

      elt.iterator = 0;
      increment    = 1;
    }

    /* increment */

    elt.iterator += increment;

    if (elt.iterator >= elt.lut.length) {

      elt.iterator = elt.lut.length - 1;
    }

    /* fetch the step value */

    return elt.value();
  }
}
